/** Formatting helpers. Everything numeric here is rendered with tabular-nums. */

function pad(value: number): string {
  return value < 10 ? `0${value}` : String(value);
}

/**
 * `12:04` under an hour, `1:02:04` over it. Used for durations, the recorder
 * clock, the player scrubber and comment timecodes — one format everywhere so
 * a timecode is always recognisable as one.
 */
export function timecode(totalSeconds: number | null | undefined): string {
  if (totalSeconds === null || totalSeconds === undefined || !Number.isFinite(totalSeconds)) {
    return '--:--';
  }
  const seconds = Math.max(0, Math.floor(totalSeconds));
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const rest = seconds % 60;
  return hours > 0 ? `${hours}:${pad(minutes)}:${pad(rest)}` : `${minutes}:${pad(rest)}`;
}

/** The recorder clock, which wants a leading zero on minutes. */
export function clock(totalSeconds: number): string {
  const seconds = Math.max(0, Math.floor(totalSeconds));
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const rest = seconds % 60;
  return hours > 0
    ? `${hours}:${pad(minutes)}:${pad(rest)}`
    : `${pad(minutes)}:${pad(rest)}`;
}

/** Parse `2:14` / `1:02:14` / `134` into seconds. Null when unparseable. */
export function parseTimecode(input: string): number | null {
  const parts = input.trim().split(':');
  if (parts.length === 0 || parts.length > 3) return null;
  let seconds = 0;
  for (const part of parts) {
    if (!/^\d+$/.test(part)) return null;
    seconds = seconds * 60 + Number(part);
  }
  return Number.isFinite(seconds) ? seconds : null;
}

export function bytes(value: number): string {
  if (value < 1024) return `${value} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let scaled = value / 1024;
  let unit = 0;
  while (scaled >= 1024 && unit < units.length - 1) {
    scaled /= 1024;
    unit += 1;
  }
  return `${scaled < 10 ? scaled.toFixed(1) : Math.round(scaled)} ${units[unit]}`;
}

const RELATIVE_STEPS: Array<[limitSeconds: number, divisor: number, unit: Intl.RelativeTimeFormatUnit]> = [
  [60, 1, 'second'],
  [3600, 60, 'minute'],
  [86400, 3600, 'hour'],
  [604800, 86400, 'day'],
  [2629800, 604800, 'week'],
  [31557600, 2629800, 'month'],
];

const relative = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });

/** "3 minutes ago", "last week". Falls back to a date past a year. */
export function timeAgo(iso: string | null | undefined): string {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const deltaSeconds = (then - Date.now()) / 1000;
  const magnitude = Math.abs(deltaSeconds);
  for (const [limit, divisor, unit] of RELATIVE_STEPS) {
    if (magnitude < limit) return relative.format(Math.round(deltaSeconds / divisor), unit);
  }
  return relative.format(Math.round(deltaSeconds / 31557600), 'year');
}

const dateFormatter = new Intl.DateTimeFormat(undefined, {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
});

export function shortDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const value = new Date(iso);
  return Number.isNaN(value.getTime()) ? '' : dateFormatter.format(value);
}

const dateTimeFormatter = new Intl.DateTimeFormat(undefined, {
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
});

export function dateTime(iso: string | null | undefined): string {
  if (!iso) return '';
  const value = new Date(iso);
  return Number.isNaN(value.getTime()) ? '' : dateTimeFormatter.format(value);
}

/**
 * The recorder's default title, matching what slate-api would have named it:
 * "<name> — Aug 18, 2:14 PM".
 */
export function defaultRecordingTitle(name: string | null | undefined): string {
  const when = dateTimeFormatter.format(new Date());
  const who = name?.trim();
  return who ? `${who} — ${when}` : `Recording — ${when}`;
}

export function pluralize(count: number, singular: string, plural?: string): string {
  return count === 1 ? singular : (plural ?? `${singular}s`);
}

export function countLabel(count: number, singular: string, plural?: string): string {
  return `${count.toLocaleString()} ${pluralize(count, singular, plural)}`;
}
